// Dialog to save issue resolution to Knowledge Base for future cases
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { BookOpen, Plus, Trash2, Loader2 } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'

interface SaveToKBDialogProps {
  issueId: string
  issueTitle: string
  issueDescription: string
  d365Version: string
  d365Module: string
  category: string
  resolutionSummary?: string
  resolutionSteps?: { order: number; instruction: string; expectedResult?: string }[]
  onSaved: () => void
  onClose: () => void
}

interface StepInput {
  instruction: string
  expectedResult: string
}

export function SaveToKBDialog({
  issueId, issueTitle, issueDescription,
  d365Version, d365Module, category,
  resolutionSummary: initialSummary, resolutionSteps: initialSteps,
  onSaved, onClose,
}: SaveToKBDialogProps): JSX.Element {
  const { user } = useAuth()
  const [title, setTitle] = useState(issueTitle)
  const [description, setDescription] = useState(issueDescription)
  const [resolutionSummary, setResolutionSummary] = useState(initialSummary || '')
  const [steps, setSteps] = useState<StepInput[]>(
    initialSteps && initialSteps.length > 0
      ? initialSteps.map((s) => ({ instruction: s.instruction, expectedResult: s.expectedResult || '' }))
      : [{ instruction: '', expectedResult: '' }]
  )
  const [saving, setSaving] = useState(false)

  const addStep = () => setSteps((prev) => [...prev, { instruction: '', expectedResult: '' }])

  const removeStep = (idx: number) => setSteps((prev) => prev.filter((_, i) => i !== idx))

  const updateStep = (idx: number, field: keyof StepInput, value: string) => {
    setSteps((prev) => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s))
  }

  const handleSave = async () => {
    if (!user || !title.trim() || !resolutionSummary.trim()) return
    setSaving(true)

    try {
      const zeroEmbedding = new Array(1536).fill(0)

      // Create KB entry
      const { data: kbEntry, error: kbError } = await supabase
        .from('knowledge_entries')
        .insert({
          organization_id: user.organizationId,
          created_by: user.id,
          title: title.trim(),
          description: description.trim(),
          description_embedding: JSON.stringify(zeroEmbedding),
          content: resolutionSummary.trim(),
          d365_versions: d365Version ? [d365Version] : [],
          d365_modules: d365Module ? [d365Module] : [],
          category: category || 'other',
          source_type: 'issue_resolution',
          source_id: issueId,
          tags: [],
          is_validated: false,
          is_archived: false,
        } as never)
        .select('id')
        .single()

      if (kbError) throw kbError

      // Create resolution method with steps
      const validSteps = steps.filter((s) => s.instruction.trim())
      if (kbEntry && validSteps.length > 0) {
        await supabase.from('resolution_methods').insert({
          organization_id: user.organizationId,
          knowledge_entry_id: kbEntry.id,
          created_by: user.id,
          title: `Resolution: ${title.trim()}`,
          description: resolutionSummary.trim(),
          steps: validSteps.map((s, i) => ({
            order: i + 1,
            instruction: s.instruction.trim(),
            expectedResult: s.expectedResult.trim() || undefined,
          })),
          d365_versions: d365Version ? [d365Version] : [],
          d365_modules: d365Module ? [d365Module] : [],
          issue_categories: category ? [category] : [],
          risk_level: 'low',
          requires_backup: false,
          requires_testing: false,
          is_active: true,
        } as never)
      }

      onSaved()
    } catch (err) {
      console.error('Failed to save to KB:', err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="rounded-lg border border-kaycoo-teal-light bg-white p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-kaycoo-teal-dark">
          <BookOpen className="h-4 w-4" /> Save to Knowledge Base
        </h3>
        <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
      </div>

      <div className="space-y-3">
        <div>
          <Label className="text-xs">KB Entry Title</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 border-kaycoo-white-3" />
        </div>
        <div>
          <Label className="text-xs">Problem Description</Label>
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="mt-1 border-kaycoo-white-3" />
        </div>
        <div>
          <Label className="text-xs">Resolution Summary *</Label>
          <Textarea value={resolutionSummary} onChange={(e) => setResolutionSummary(e.target.value)}
            rows={3} className="mt-1 border-kaycoo-white-3" placeholder="Describe how this issue was resolved..." />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Resolution Steps</Label>
            <Button variant="ghost" size="sm" onClick={addStep} className="h-6 gap-1 text-xs text-kaycoo-teal-dark">
              <Plus className="h-3 w-3" /> Add Step
            </Button>
          </div>
          {steps.map((step, idx) => (
            <div key={idx} className="flex gap-2 items-start">
              <Badge variant="outline" className="mt-1.5 shrink-0 text-xs">{idx + 1}</Badge>
              <div className="flex-1 space-y-1">
                <Input value={step.instruction} onChange={(e) => updateStep(idx, 'instruction', e.target.value)}
                  placeholder="What to do..." className="border-kaycoo-white-3 text-xs" />
                <Input value={step.expectedResult} onChange={(e) => updateStep(idx, 'expectedResult', e.target.value)}
                  placeholder="Expected result (optional)" className="border-kaycoo-white-3 text-xs" />
              </div>
              {steps.length > 1 && (
                <Button variant="ghost" size="sm" onClick={() => removeStep(idx)} className="mt-1 h-6 w-6 p-0 text-kaycoo-charcoal-2">
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2 text-xs text-kaycoo-charcoal-2">
          <span>Tags:</span>
          {d365Version && <Badge variant="outline" className="text-[10px]">v{d365Version}</Badge>}
          {d365Module && <Badge variant="outline" className="text-[10px]">{d365Module}</Badge>}
          {category && <Badge variant="outline" className="text-[10px]">{category}</Badge>}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-kaycoo-white-3">
        <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
        <Button size="sm" onClick={handleSave} disabled={saving || !title.trim() || !resolutionSummary.trim()}
          className="gap-1.5 bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BookOpen className="h-3.5 w-3.5" />}
          Save to KB
        </Button>
      </div>
    </div>
  )
}
